import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import settings from 'src/settings';
import type {
  UserAdminListUser,
  UserAdminListUserUpstream,
} from 'src/types/user';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { mapUserAdminListUserToProps } from 'src/components/app/context/UserContext/helper';

interface DataResponse {
  results: UserAdminListUserUpstream[];
  count: number;
  next: string;
  previous: string;
}

export interface QueryResponse extends Omit<DataResponse, 'results'> {
  users: UserAdminListUser[];
}

const useUserAdminAllSurveysQuery = (
  enabled = false,
  searchString = '',
  pageNo = 1,
) => {
  const { t } = useTranslation('common');
  const endPoint = apiPaths.ADMIN.USER_ALL_SURVEYS;
  const pageSize = useMemo(
    () => Number(settings.VITE_APP_PAGINATION_THRESHOLD_USER_LIST) || 100,
    [settings],
  );

  return useQuery<QueryResponse>({
    cacheTime: 0,
    queryKey: [endPoint, pageNo, pageSize, searchString],
    enabled: enabled && !!searchString,
    queryFn: ({ signal }) =>
      HttpService.get(
        endPoint,
        {
          search: searchString,
          page: pageNo,
          pageSize: pageSize,
        },
        true,
        { signal },
      )
        .then(({ data }: AxiosResponse<DataResponse>) => {
          if (!data) return data;

          const { results, count, next, previous } = data;
          return {
            users: results.map(mapUserAdminListUserToProps),
            count,
            next,
            previous,
          };
        })
        .catch(({ response }) => {
          const errorMsg = response?.data?.detail || t('networkError');
          return Promise.reject(errorMsg || 'An unknown error occurred.');
        }),
  });
};

export default useUserAdminAllSurveysQuery;
